// UI theme selected at deploy time via window.__UI_THEME__ (set in public/config.js,
// loaded before the bundle). No rebuild needed to switch themes.
//
//   'minimal' (default) — flat, discreto, monocromático, pouca sombra/raio.
//   'saas'              — visual polido: cantos arredondados, sombra, áreas tingidas.
//
// Components read UI_THEME once and pick their classes via `themed({ minimal, saas })`.

export type UiTheme = 'minimal' | 'saas';
export type ColorMode = 'light' | 'dark' | 'system';

declare global {
  interface Window {
    __UI_THEME__?: string;
    __COLOR_MODE__?: string;
  }
}

const resolve = (): UiTheme => {
  const v = typeof window !== 'undefined' ? window.__UI_THEME__ : undefined;
  return v === 'saas' ? 'saas' : 'minimal';
};

export const UI_THEME: UiTheme = resolve();

// Expõe o tema ao CSS (styles/tokens.css varia raio e cor primária por tema).
if (typeof document !== 'undefined') {
  document.documentElement.dataset.uiTheme = UI_THEME;
}

const resolveColorMode = (): ColorMode => {
  const v = typeof window !== 'undefined' ? window.__COLOR_MODE__ : undefined;
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
};

export const COLOR_MODE: ColorMode = resolveColorMode();

const shouldUseDarkMode = (mode: ColorMode): boolean => {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
};

export const applyColorMode = (mode: ColorMode = COLOR_MODE): void => {
  if (typeof document === 'undefined') return;

  const isDark = shouldUseDarkMode(mode);
  document.documentElement.classList.toggle('dark', isDark);
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
};

/** Modo escuro padrão do deploy (`__COLOR_MODE__`): vale enquanto o usuário não escolheu nada pelo botão de tema. */
export const defaultDark = (): boolean => shouldUseDarkMode(COLOR_MODE);

// Primeira pintura já no modo do deploy. Quem decide de fato é ui/darkMode.ts (escolha do usuário > padrão do
// deploy), que roda em seguida em main.tsx e também acompanha o sistema — um listener aqui reaplicaria o
// sistema por cima de uma escolha explícita.
applyColorMode();

/** Picks the value for the active theme. `themed({ minimal: 'a', saas: 'b' })`. */
export const themed = <T,>(variants: Record<UiTheme, T>): T => variants[UI_THEME];
